/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import { Button, Checkbox, CheckboxGroup, Dialog, Link, TextField, Typography2 } from '@coursera/cds-core';

import { accountDeleted } from 'bundles/account-settings/actions/SettingsActionCreators';
import { useNaptime } from 'bundles/naptimejs';
/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import UserAccountDelete from 'bundles/naptimejs/resources/userAccountDelete.v1';

import _t from 'i18n!nls/account-settings';

const DISCLAIMER_LOGIN = 'login';
const DISCLAIMER_ANONYMIZED = 'anonymized';
const DISCLAIMER_CERTIFICATES = 'certificates';

const ALL_DISCLAIMERS = [DISCLAIMER_LOGIN, DISCLAIMER_ANONYMIZED, DISCLAIMER_CERTIFICATES];

const styles = {
  disclaimerGroup: css`
    margin: var(--cds-spacing-200) 0;
  `,
  passwordInput: css`
    width: 100%;
  `,
  actions: css`
    display: flex;
    gap: var(--cds-spacing-150);
    justify-content: flex-start;
  `,
};

type Props = {
  userId: number;
  onClose: () => void;
  onDelete?: () => void;
};

const AccountDeletionModal = ({ onClose, userId: id, onDelete }: Props) => {
  const passwordInputRef = useRef<HTMLInputElement | null>(null);
  const [isPasswordEmpty, setIsPasswordEmtpy] = useState(true);
  const [isInProgress, setIsInProgress] = useState(false);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [agreedItems, setAgreedItems] = useState<string[]>([]);
  const { naptime } = useNaptime(() => ({}));

  const isAgreed = agreedItems.length === ALL_DISCLAIMERS.length;

  useEffect(() => {
    if (isAgreed) {
      passwordInputRef.current?.focus();
    }
  }, [isAgreed]);

  useEffect(() => {
    if (errorMessage) {
      passwordInputRef.current?.focus();
    }
  }, [errorMessage]);

  const handleDisclaimerChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const { value } = event.target;
    setAgreedItems((prev) => (checked ? [...prev, value] : prev.filter((v) => v !== value)));
  };

  const handlePasswordChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setIsPasswordEmtpy(evt.target.value.length === 0);
  };

  const handleDeleteAccount = () => {
    setIsInProgress(true);
    setErrorMessage('');
    const password = passwordInputRef.current?.value;

    const deleteUserAccount = UserAccountDelete.action(
      'refundAndDeleteAccount',
      {
        password,
      },
      { id }
    );

    naptime
      .executeMutation(deleteUserAccount)
      .then(() => {
        accountDeleted();
        if (onDelete) {
          onDelete();
        }
      })
      .catch((error) => {
        if (error.status === 429) {
          setIsRateLimited(true);
          setErrorMessage(
            _t(
              'Account delete has been attempted recently with incorrect password. Please wait 5 minutes to attempt again.'
            )
          );
        } else {
          setErrorMessage(_t('Please re-enter your password and try again.'));
        }
        setIsInProgress(false);
      });
  };

  return (
    <Dialog onClose={onClose} open>
      <Dialog.HeadingGroup>{_t('Deleting Your Account')}</Dialog.HeadingGroup>
      <Dialog.Content>
        <Typography2 variant="bodyPrimary" component="p">
          {_t(
            `If you're sure you want to delete your Coursera account,

you'll need to enter your password and agree to the following:`
          )}
        </Typography2>

        <CheckboxGroup value={agreedItems} onChange={handleDisclaimerChange} css={styles.disclaimerGroup}>
          <Checkbox value={DISCLAIMER_LOGIN} label={_t('You will no longer be able to log into your account')} />

          <Checkbox
            value={DISCLAIMER_ANONYMIZED}
            label={_t(
              // eslint-disable-next-line no-restricted-syntax
              'Personally identifiable information in your profile will be removed. Discussion forum posts, peer review contributions, and other course activity will be anonymized but not deleted.'
            )}
          />

          <Checkbox
            value={DISCLAIMER_CERTIFICATES}
            label={_t(
              'Any certificates earned will be deleted. If you have posted your certificates on LinkedIn, the links back to them will no longer work.'
            )}
          />

          <Link href="/accomplishments">{_t('Download your certificates (PDF)')}</Link>
        </CheckboxGroup>

        <TextField
          id="deletion-password-input"
          type="password"
          placeholder={_t('Enter your password...')}
          inputRef={passwordInputRef}
          css={styles.passwordInput}
          onChange={handlePasswordChange}
          disabled={!isAgreed || isInProgress}
          label={_t('Current Password')}
          validationStatus={errorMessage ? 'error' : undefined}
          validationLabel={errorMessage || undefined}
        />
      </Dialog.Content>
      <Dialog.Actions>
        <div css={styles.actions}>
          <Button
            disabled={isPasswordEmpty || !isAgreed || isInProgress || isRateLimited}
            onClick={handleDeleteAccount}
            variant="primary"
          >
            {isInProgress ? _t('Deleting Account') : _t('Delete Account')}
          </Button>

          <Button onClick={onClose} type="button" variant="ghost">
            {_t('Cancel')}
          </Button>
        </div>
      </Dialog.Actions>
    </Dialog>
  );
};

export default AccountDeletionModal;
